import { useTranslation } from '@/contexts/TranslationContext'

interface BillingCompletionStepProps {
  workspaceName: string
  userName: string
  onFinish: () => void
  isLoading: boolean
}

export function BillingCompletionStep({
  workspaceName,
  userName,
  onFinish,
  isLoading
}: BillingCompletionStepProps) {
  const { t } = useTranslation()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="mb-6 flex justify-center">
          <svg className="w-16 h-16 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
          {t('onboarding.billing.completion.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('onboarding.billing.completion.subtitle')}
        </p>
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-lg p-8 space-y-6">
        <div className="space-y-4">
          {/* User Info */}
          <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-slate-600 dark:text-slate-400">{t('onboarding.billing.completion.userName')}</span>
            </div>
            <span className="font-medium text-slate-900 dark:text-white">{userName}</span>
          </div>

          {/* Workspace Info */}
          <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m0 0l8-4m0 0l8 4m0 0v10l-8 4-8-4V7m0 0L4 5m8 4v10m8-10L12 9m0 0l-8-4" />
                </svg>
              </div>
              <span className="text-slate-600 dark:text-slate-400">{t('onboarding.billing.completion.workspaceName')}</span>
            </div>
            <span className="font-medium text-slate-900 dark:text-white">{workspaceName}</span>
          </div>

          {/* Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <span className="text-slate-600 dark:text-slate-400">{t('onboarding.billing.completion.status')}</span>
            </div>
            <span className="font-medium text-green-600 dark:text-green-400">{t('onboarding.billing.completion.ready')}</span>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-6 space-y-4">
        <h3 className="font-medium text-slate-900 dark:text-white mb-3">
          {t('onboarding.billing.completion.nextSteps')}
        </h3>
        <ul className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{t('onboarding.billing.completion.step1')}</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{t('onboarding.billing.completion.step2')}</span>
          </li>
          <li className="flex items-start gap-2">
            <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <span>{t('onboarding.billing.completion.step3')}</span>
          </li>
        </ul>
      </div>

      {/* Button */}
      <div className="flex justify-center">
        <button
          onClick={onFinish}
          disabled={isLoading}
          className="px-12 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-400 text-white rounded-lg font-medium transition-colors"
        >
          {isLoading ? t('onboarding.billing.loading') : t('onboarding.billing.completion.finishButton')}
        </button>
      </div>
    </div>
  )
}
